import { useMemo, type ReactNode } from 'react';
import { Link, useLocation } from 'wouter';
import { useQueryClient } from '@tanstack/react-query';
import {
  Users,
  Inbox,
  MessageCircle,
  Shield,
  LogOut,
  BookOpen,
  ChevronsUpDown,
  UserCircle,
  FileSpreadsheet,
  CreditCard,
  Gauge,
  Banknote,
  LayoutDashboard,
  Contrast,
  Building2,
} from 'lucide-react';
import { api } from '@/lib/api';
import { useCurrentAdmin } from '@/hooks/use-current-admin';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { useTheme } from '@/components/theme-provider';

interface AdminShellProps {
  children: ReactNode;
  /**
   * Si true, el contenido va a full-bleed (padding 0). Útil en páginas tipo
   * "editor" donde el viewport es premium (ej. revisión de factura con imagen
   * + form lado a lado). Ya no hay header global: cada página pone su propio
   * título; el tema se cambia desde el menú de usuario; el sidebar se colapsa
   * con el rail (desktop) o el trigger mobile.
   */
  hideHeader?: boolean;
}

// Navegación por MÓDULOS. Cada módulo agrupa su submenú operativo.
const navSections = [
  {
    label: 'General',
    items: [
      { href: '/admin/dashboard', label: 'Inicio', icon: LayoutDashboard },
      { href: '/admin/tenants', label: 'Contactos', icon: Users },
    ],
  },
  {
    label: 'Contabilidad',
    items: [
      { href: '/admin/review-queue', label: 'Cola de revisión', icon: Inbox },
      { href: '/admin/declarations', label: 'Declaraciones', icon: FileSpreadsheet },
      { href: '/admin/proveedores', label: 'Proveedores', icon: Building2 },
    ],
  },
  {
    label: 'Cobros',
    items: [{ href: '/admin/cobros', label: 'Cobros', icon: Banknote }],
  },
  {
    label: 'Configuración',
    items: [
      { href: '/admin/plans', label: 'Planes', icon: CreditCard },
      { href: '/admin/messaging', label: 'Mensajería', icon: MessageCircle },
      { href: '/admin/rag', label: 'Conocimiento', icon: BookOpen },
      { href: '/admin/ia', label: 'IA', icon: Gauge },
      { href: '/admin/admins', label: 'Admins', icon: Shield },
    ],
  },
];

export function AdminShell({ children, hideHeader = false }: AdminShellProps) {
  return (
    <SidebarProvider>
      <ActuoSidebar />
      <SidebarInset>
        {hideHeader ? (
          <div className="flex flex-1 flex-col gap-4 p-0">{children}</div>
        ) : (
          // En mobile el sidebar es un sheet. El trigger va ABSOLUTO en el gutter
          // superior-izq y SOLO el <h1> del título lleva padding-left para
          // despejarlo → el resto queda flush. Funciona sea el header div simple o
          // flex (apuntamos al h1). En desktop se oculta, sin padding.
          <div className="relative flex-1 px-2 py-4 md:p-6">
            <SidebarTrigger className="absolute left-2 top-4 z-10 md:hidden" />
            <div className="flex flex-col gap-4 max-md:[&>*:first-child_h1]:pl-9">{children}</div>
          </div>
        )}
      </SidebarInset>
    </SidebarProvider>
  );
}

function ActuoSidebar() {
  const [location] = useLocation();
  return (
    <Sidebar collapsible="icon" variant="inset">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <Link href="/admin/dashboard">
                <div className="flex aspect-square size-8 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-white">
                  <img src="/actuo-icon.png" alt="Actuo" className="size-6 object-contain" />
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
                  <span className="truncate font-semibold">Actuo</span>
                  <span className="truncate text-xs">Panel admin</span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {navSections.map((section) => (
          <SidebarGroup key={section.label}>
            <SidebarGroupLabel>{section.label}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {section.items.map((item) => {
                  const active = location.startsWith(item.href);
                  const Icon = item.icon;
                  return (
                    <SidebarMenuItem key={item.href}>
                      <SidebarMenuButton asChild isActive={active} tooltip={item.label}>
                        <Link href={item.href}>
                          <Icon />
                          <span>{item.label}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <UserMenu />
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}

function UserMenu() {
  const { data: me } = useCurrentAdmin();
  const [, navigate] = useLocation();
  const qc = useQueryClient();
  const { isMobile } = useSidebar();
  const { setTheme } = useTheme();

  const initials = useMemo(() => {
    if (!me?.fullName) return 'A';
    return me.fullName
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]!.toUpperCase())
      .join('');
  }, [me?.fullName]);

  async function logout() {
    await api('/api/auth/logout', { method: 'POST' });
    qc.clear();
    navigate('/login');
  }

  if (!me) return null;

  return (
    <SidebarMenuItem>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton
            size="lg"
            tooltip={me.fullName}
            className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
          >
            <Avatar className="size-8 shrink-0 rounded-lg">
              <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
            </Avatar>
            <div className="grid min-w-0 flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
              <span className="truncate font-medium">{me.fullName}</span>
              <span className="truncate text-xs text-sidebar-foreground/70">{me.email}</span>
            </div>
            <ChevronsUpDown className="ml-auto size-4 shrink-0 group-data-[collapsible=icon]:hidden" />
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
          side={isMobile ? 'bottom' : 'right'}
          align="end"
          sideOffset={4}
        >
          <DropdownMenuLabel className="p-0 font-normal">
            <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
              <Avatar className="size-8 rounded-lg">
                <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">{me.fullName}</span>
                <span className="truncate text-xs text-muted-foreground">{me.email}</span>
              </div>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            <DropdownMenuItem asChild>
              <Link href="/admin/profile">
                <UserCircle />
                Mi perfil
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <Contrast />
                Tema
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <DropdownMenuItem onClick={() => setTheme('light')}>Claro</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme('dark')}>Oscuro</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme('system')}>Sistema</DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuItem variant="destructive" onClick={logout}>
            <LogOut />
            Cerrar sesión
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  );
}

